/**
 * SchoolLifecycle - Promotions, Graduation, Alumni, Timeline
 * End-to-end student journey management
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  ArrowUpCircle, GraduationCap, Users, Search, Loader2,
  ChevronRight, UserCheck, UserMinus, Clock, CreditCard,
  ClipboardCheck, Flag, School, ArrowUp, Pause, Banknote,
  CheckCircle2, UserPlus, Calendar, AlertTriangle, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};


// ==================== RESIT LIST ====================
// Every resit entered, and what became of it. Resits whose results are in
// close themselves — the list reconciles against published marks each time it
// is drawn, so nobody has to remember to tick anything.
const ResitList = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['resits', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/resits`),
    enabled: !!tenantId,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api(`/tenants/${tenantId}/resits/${id}?status=${status}`, { method: 'PUT' }),
    onSuccess: (d) => {
      toast.success(`Marked ${d.status}`);
      queryClient.invalidateQueries({ queryKey: ['resits'] });
      queryClient.invalidateQueries({ queryKey: ['promotion-preview'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = data?.items || [];
  const chip = { scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
                 sat: 'bg-sky-50 text-sky-700 border-sky-200',
                 passed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                 withdrawn: 'bg-slate-50 text-slate-600 border-slate-200' };

  return (
    <Card data-testid="resit-list-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-5 w-5 text-rose-600" /> Resits
        </CardTitle>
        <CardDescription>
          Students entered to sit a module again. A published pass closes the resit and lifts the promotion hold on its own.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="resit-list-empty">
            No resits entered. Held students can be entered from the promotion screen.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Mark it</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`resit-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.student_name}</TableCell>
                  <TableCell className="text-sm">{r.subject_name} <span className="text-xs text-slate-400">{r.subject_code}</span></TableCell>
                  <TableCell className="text-xs">{r.scheduled_for || 'to be set'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={chip[r.status] || ''}>
                      {r.status}
                      {r.status === 'passed' && r.percentage != null ? ` · ${r.percentage}%` : ''}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === 'scheduled' || r.status === 'sat' ? (
                      <div className="flex justify-end gap-1">
                        {r.status === 'scheduled' && (
                          <Button size="sm" variant="outline"
                                  onClick={() => setStatus.mutate({ id: r.id, status: 'sat' })}
                                  data-testid={`resit-mark-sat-${r.id}`}>Sat</Button>
                        )}
                        <Button size="sm" variant="ghost"
                                onClick={() => setStatus.mutate({ id: r.id, status: 'withdrawn' })}
                                data-testid={`resit-withdraw-${r.id}`}>Withdraw</Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">closed</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};


// ==================== RESIT ENTRY ====================
// A held student's way out: enter them for the paper again. This does not
// clear the hold — the hold clears when the resit mark is published.
const ResitDialog = ({ tenantId, student, onClose }) => {
  const queryClient = useQueryClient();
  const [subjectId, setSubjectId] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [note, setNote] = useState('');

  const shortfall = student?.shortfall || [];

  const mutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/resits`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (d) => {
      toast.success(`${student.first_name} entered for the ${d.subject_name} resit`, {
        description: d.scheduled_for ? `Scheduled for ${d.scheduled_for} — they have been told` : 'They have been told; set a date when you have one',
      });
      queryClient.invalidateQueries({ queryKey: ['promotion-preview'] });
      close();
    },
    onError: (e) => toast.error(e.message),
  });

  const close = () => { setSubjectId(''); setScheduledFor(''); setNote(''); onClose(); };

  return (
    <Dialog open={!!student} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent data-testid="resit-dialog">
        <DialogHeader>
          <DialogTitle>Enter {student?.first_name} {student?.last_name} for a resit</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Which module?</Label>
            <Select value={subjectId} onValueChange={setSubjectId}>
              <SelectTrigger className="mt-1" data-testid="resit-subject-select">
                <SelectValue placeholder="Choose the module to be sat again" />
              </SelectTrigger>
              <SelectContent>
                {shortfall.map(s => (
                  <SelectItem key={s.subject_id} value={s.subject_id} disabled={s.resit_scheduled}>
                    {s.name} — {s.status === 'failed' ? 'failed' : s.status === 'incomplete' ? 'part assessed' : 'not sat'}
                    {s.resit_scheduled ? ' (already entered)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Date of the resit (optional)</Label>
            <Input type="date" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)}
                   className="mt-1" data-testid="resit-date-input" />
          </div>
          <div>
            <Label className="text-xs">Anything they should know</Label>
            <Input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="e.g. bring your own Bible; revision class on Thursday"
                   className="mt-1" data-testid="resit-note-input" />
          </div>
          <p className="text-xs text-slate-500">
            They will find this in their inbox. The promotion stays held until the resit is marked.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button
            onClick={() => {
              if (!subjectId) { toast.error('Choose the module to be sat again'); return; }
              mutation.mutate({ student_id: student.id, subject_id: subjectId, scheduled_for: scheduledFor, note });
            }}
            disabled={mutation.isPending}
            data-testid="confirm-resit-btn"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Enter for resit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== PROMOTIONS TAB ====================
const PromotionsPanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [mappings, setMappings] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [note, setNote] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [resitFor, setResitFor] = useState(null);

  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['promotion-preview', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/promotions/preview`),
    enabled: !!tenantId,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
    enabled: !!tenantId,
  });

  const { data: academicYears = [] } = useQuery({
    queryKey: ['school-academic-years', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/academic-years`),
    enabled: !!tenantId,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['promotion-history', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/promotions/history`),
    enabled: !!tenantId,
  });

  const { data: batchDetails } = useQuery({
    queryKey: ['promotion-batch', tenantId, selectedBatchId],
    queryFn: () => api(`/tenants/${tenantId}/promotions/${selectedBatchId}/details`),
    enabled: !!tenantId && !!selectedBatchId,
  });

  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [overrideRequirements, setOverrideRequirements] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const executeMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/promotions/execute`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (d) => {
      toast.success(d.message);
      if (d.held > 0) {
        toast.warning(`${d.held} student${d.held === 1 ? '' : 's'} held back — results outstanding`);
      }
      setShowConfirm(false);
      setMappings([]);
      setExcludedIds([]);
      setNote('');
      setOverrideRequirements(false);
      setOverrideReason('');
      queryClient.invalidateQueries({ queryKey: ['promotion-preview'] });
      queryClient.invalidateQueries({ queryKey: ['promotion-history'] });
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMapping = (fromClassId, toClassId) => {
    setMappings(prev => {
      const existing = prev.find(m => m.from_class_id === fromClassId);
      if (existing) {
        if (!toClassId) return prev.filter(m => m.from_class_id !== fromClassId);
        return prev.map(m => m.from_class_id === fromClassId ? { ...m, to_class_id: toClassId } : m);
      }
      if (toClassId) return [...prev, { from_class_id: fromClassId, to_class_id: toClassId }];
      return prev;
    });
  };

  const toggleExclude = (studentId) => {
    setExcludedIds(prev => prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]);
  };

  const handleExecute = () => {
    if (!selectedAcademicYear) { toast.error('Select target academic year'); return; }
    if (mappings.length === 0) { toast.error('Set at least one class mapping'); return; }
    if (overrideRequirements && overrideReason.trim().length < 10) {
      toast.error('Say why these students are being promoted'); return;
    }
    executeMutation.mutate({
      academic_year_id: selectedAcademicYear,
      mappings,
      excluded_student_ids: excludedIds,
      note,
      override_requirements: overrideRequirements,
      override_reason: overrideReason,
    });
  };

  const previewClasses = preview?.classes || [];
  const totalStudents = preview?.total_students || 0;
  const mappedStudents = previewClasses
    .filter(c => mappings.find(m => m.from_class_id === c.class_id))
    .reduce((sum, c) => sum + c.student_count, 0);
  // Students in a mapped class who still have a module failed or unsat.
  const notCleared = previewClasses
    .filter(c => mappings.find(m => m.from_class_id === c.class_id))
    .flatMap(c => (c.students || []).filter(s => s.clear_to_progress === false
                                                 && !excludedIds.includes(s.id)));
  const notClearedCount = notCleared.length;

  return (
    <div className="space-y-6" data-testid="promotions-panel">
      <Tabs defaultValue="promote">
        <TabsList className="overflow-x-auto flex-nowrap" data-testid="promotion-tabs">
          <TabsTrigger value="promote">Promote Students</TabsTrigger>
          <TabsTrigger value="history">Promotion History</TabsTrigger>
        </TabsList>

        <TabsContent value="promote" className="space-y-4 mt-4">
          {/* Controls */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Year-End Promotion</CardTitle>
              <CardDescription>Map each class to its next-year equivalent, then execute bulk promotion</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Target Academic Year *</Label>
                  <Select value={selectedAcademicYear} onValueChange={setSelectedAcademicYear}>
                    <SelectTrigger data-testid="promotion-year-select"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>
                      {academicYears.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. 2025 year-end promotion" data-testid="promotion-note-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Class Mappings */}
          {previewLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Class Mappings</CardTitle>
                    <CardDescription>{totalStudents} total students across {previewClasses.length} classes</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">{mappings.length} mapped</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Current Class</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Promote To</TableHead>
                      <TableHead>Excluded</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewClasses.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No classes with active students</TableCell></TableRow>
                    ) : previewClasses.map(c => {
                      const currentMapping = mappings.find(m => m.from_class_id === c.class_id);
                      const excludedInClass = c.students.filter(s => excludedIds.includes(s.id));
                      return (
                        <React.Fragment key={c.class_id}>
                          <TableRow data-testid={`promotion-row-${c.class_id}`}>
                            <TableCell className="font-medium">{c.class_name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{c.student_count}</Badge>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={currentMapping?.to_class_id || ''}
                                onValueChange={v => updateMapping(c.class_id, v)}
                              >
                                <SelectTrigger className="w-40" data-testid={`promote-to-${c.class_id}`}>
                                  <SelectValue placeholder="Select class" />
                                </SelectTrigger>
                                <SelectContent>
                                  {classes.filter(cl => cl.id !== c.class_id).map(cl => (
                                    <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {excludedInClass.length > 0 && (
                                <Badge variant="outline" className="text-amber-600">{excludedInClass.length} retained</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Expandable student list for exclusion */}
                          {currentMapping && c.students.length > 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="bg-slate-50 py-2 px-6">
                                <div className="flex flex-wrap gap-2">
                                  {c.students.map(s => (
                                    <button
                                      key={s.id}
                                      onClick={() => toggleExclude(s.id)}
                                      title={s.clear_to_progress === false
                                        ? `Has not cleared the year — ${s.progression_detail}`
                                        : (s.unassessed ? 'No published results yet' : '')}
                                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                        excludedIds.includes(s.id)
                                          ? 'bg-amber-100 border-amber-300 text-amber-800'
                                          : s.clear_to_progress === false
                                            ? 'bg-rose-50 border-rose-200 text-rose-700 hover:border-rose-300'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'
                                      }`}
                                      data-testid={`toggle-student-${s.id}`}
                                    >
                                      {s.first_name} {s.last_name}
                                      {excludedIds.includes(s.id) ? ' (retained)' : ''}
                                      {!excludedIds.includes(s.id) && s.clear_to_progress === false ? ' · not cleared' : ''}
                                    </button>
                                  ))}
                                </div>
                                <p className="text-xs text-slate-400 mt-1">Click a student to retain (exclude from promotion). Students shown in red still have a module failed or unsat.</p>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>

                {mappings.length > 0 && (
                  <div className="mt-4 flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-indigo-600">{mappedStudents - excludedIds.length - notClearedCount}</span> students will be promoted,{' '}
                      <span className="font-semibold text-amber-600">{excludedIds.length}</span> retained
                      {notClearedCount > 0 && (
                        <>, <span className="font-semibold text-rose-600" data-testid="promotion-not-cleared-count">{notClearedCount}</span> held (year not cleared)</>
                      )}
                    </p>
                    <Button onClick={() => setShowConfirm(true)} data-testid="execute-promotion-btn">
                      <ArrowUpCircle className="h-4 w-4 mr-2" />
                      Execute Promotion
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Confirm Dialog */}
          <Dialog open={showConfirm} onOpenChange={(open) => {
            setShowConfirm(open);
            // Closing the dialog puts the override back: an exception has to be
            // asked for every time, never left ticked from a cancelled attempt.
            if (!open) { setOverrideRequirements(false); setOverrideReason(''); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Bulk Promotion</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <p className="text-sm text-slate-600">
                  This will promote <strong>{mappedStudents - excludedIds.length - (overrideRequirements ? 0 : notClearedCount)}</strong> students and retain <strong>{excludedIds.length}</strong>.
                </p>
                {notClearedCount > 0 && (
                  <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                       data-testid="promotion-held-warning">
                    <p className="font-semibold">
                      {notClearedCount} student{notClearedCount === 1 ? '' : 's'} {notClearedCount === 1 ? 'has' : 'have'} not cleared the year
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
                      {notCleared.slice(0, 6).map(s => (
                        <li key={s.id} className="flex items-start justify-between gap-2">
                          <span>{s.first_name} {s.last_name} — {s.progression_detail}</span>
                          {(s.shortfall || []).length > 0 && (
                            <button
                              onClick={() => setResitFor(s)}
                              className="shrink-0 rounded-full border border-rose-300 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                              data-testid={`enter-resit-${s.id}`}
                            >
                              Enter for resit
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                    <label className="mt-2 flex items-start gap-2 text-xs">
                      <input type="checkbox" checked={overrideRequirements}
                             onChange={e => setOverrideRequirements(e.target.checked)}
                             className="mt-0.5" data-testid="promotion-override-checkbox" />
                      <span>Promote them anyway — this is recorded against the promotion</span>
                    </label>
                    {overrideRequirements && (
                      <Input value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                             placeholder="Why are they being promoted? (e.g. board resolution, resit scheduled)"
                             className="mt-2 bg-white" data-testid="promotion-override-reason" />
                    )}
                  </div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  This action cannot be easily undone. Ensure class mappings are correct.
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <Button onClick={handleExecute} disabled={executeMutation.isPending} data-testid="confirm-promotion-btn">
                  {executeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                  Confirm Promotion
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <ResitDialog tenantId={tenantId} student={resitFor} onClose={() => setResitFor(null)} />

          <ResitList tenantId={tenantId} />
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Promotion History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Promoted</TableHead>
                    <TableHead>Retained</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No promotion history</TableCell></TableRow>
                  ) : history.map(h => (
                    <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                      <TableCell className="text-sm">{new Date(h.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><Badge className="bg-green-100 text-green-700">{h.promoted_count}</Badge></TableCell>
                      <TableCell><Badge className="bg-amber-100 text-amber-700">{h.retained_count}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-48 truncate">{h.note || '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedBatchId(h.id)} data-testid={`view-batch-${h.id}`}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Batch Detail Dialog */}
          <Dialog open={!!selectedBatchId} onOpenChange={() => setSelectedBatchId(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Promotion Batch Details</DialogTitle>
              </DialogHeader>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Adm #</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(batchDetails || []).map((d, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{d.student_name}</TableCell>
                      <TableCell className="text-xs font-mono">{d.admission_number}</TableCell>
                      <TableCell>{d.from_class_name}</TableCell>
                      <TableCell>{d.to_class_name}</TableCell>
                      <TableCell>
                        <Badge className={d.action === 'promoted' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {d.action}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};


// ==================== GRADUATION TAB ====================
// ==================== CERTIFICATE AUDIT ====================
// Certificates issued before the curriculum check existed can say a student
// has graduated when the checker says they have not. This shows them. It
// changes nothing — revoking is the registrar's decision.
const CertificateAudit = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [decisionFor, setDecisionFor] = useState(null);
  const [decisionAction, setDecisionAction] = useState('revoke');
  const [decisionReason, setDecisionReason] = useState('');
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['certificate-audit', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/graduation/certificate-audit`),
    enabled: false,
  });

  const decide = useMutation({
    mutationFn: ({ certId, action, reason }) => api(
      `/tenants/${tenantId}/graduation/certificates/${certId}/decision`,
      { method: 'POST', body: JSON.stringify({ action, reason }) }),
    onSuccess: (d) => {
      toast.success(d.action === 'reissue'
        ? `Replaced — new certificate ${d.certificate?.serial}`
        : 'Certificate withdrawn, with your reason on the record');
      setDecisionFor(null);
      setDecisionReason('');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['graduation-progress'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const run = () => { setOpen(true); refetch(); };
  const rows = data?.discrepancies || [];

  return (
    <Card data-testid="certificate-audit-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-amber-600" /> Certificate audit
        </CardTitle>
        <CardDescription>
          Every graduation certificate on record whose holder no longer meets the curriculum
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" onClick={run} disabled={isFetching} data-testid="run-certificate-audit-btn">
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
          Check issued certificates
        </Button>

        {open && !isFetching && data && (
          rows.length === 0 ? (
            <p className="text-sm text-emerald-700" data-testid="certificate-audit-clean">
              {data.checked} certificate{data.checked === 1 ? '' : 's'} checked — every holder still meets their curriculum.
            </p>
          ) : (
            <div className="space-y-2" data-testid="certificate-audit-results">
              <p className="text-sm text-amber-800">
                {rows.length} of {data.checked} checked need the registrar's attention. {data.note}
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Serial</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Outstanding</TableHead>
                    <TableHead className="text-right">Decision</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.certificate_id} data-testid={`cert-audit-row-${r.certificate_id}`}>
                      <TableCell className="text-sm">
                        <p className="font-medium">{r.student_name}</p>
                        <p className="text-xs text-slate-500">
                          {[r.admission_number, r.programme].filter(Boolean).join(' · ')}
                        </p>
                        {r.certificate_name && r.certificate_name !== r.student_name && (
                          <p className="text-xs text-amber-700" data-testid={`cert-name-mismatch-${r.certificate_id}`}>
                            Printed as "{r.certificate_name}"
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.serial}</TableCell>
                      <TableCell className="text-xs">{r.issue_date || '—'}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                          {r.outstanding} subject{r.outstanding === 1 ? '' : 's'}
                        </Badge>
                        <p className="mt-1 text-slate-500">
                          {(r.subjects || []).map(s => `${s.name} (${s.status === 'failed' ? 'failed' : s.status === 'incomplete' ? 'part assessed' : 'not sat'})`).join(', ')}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline"
                                onClick={() => { setDecisionFor(r); setDecisionAction('revoke'); }}
                                data-testid={`cert-decide-${r.certificate_id}`}>
                          Decide
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        )}
      </CardContent>

      <Dialog open={!!decisionFor} onOpenChange={(o) => { if (!o) { setDecisionFor(null); setDecisionReason(''); } }}>
        <DialogContent data-testid="certificate-decision-dialog">
          <DialogHeader>
            <DialogTitle>{decisionFor?.student_name} · {decisionFor?.serial}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              {decisionFor?.outstanding} subject{decisionFor?.outstanding === 1 ? '' : 's'} outstanding:{' '}
              {(decisionFor?.subjects || []).map(s => s.name).join(', ')}
            </p>
            <div className="space-y-2">
              {[['revoke', 'Withdraw it', 'The certificate stops verifying. Nothing else changes.'],
                ['reissue', 'Withdraw and replace it', 'Only possible once the curriculum is met — otherwise the old one is still withdrawn.']]
                .map(([value, title, note]) => (
                  <label key={value} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 ${decisionAction === value ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200'}`}>
                    <input type="radio" name="cert-decision" value={value} className="mt-1"
                           checked={decisionAction === value}
                           onChange={() => setDecisionAction(value)}
                           data-testid={`cert-decision-${value}`} />
                    <span>
                      <span className="block text-sm font-medium text-slate-900">{title}</span>
                      <span className="block text-xs text-slate-500">{note}</span>
                    </span>
                  </label>
                ))}
            </div>
            <div>
              <Label className="text-xs">Why? (kept with the certificate)</Label>
              <Input value={decisionReason} onChange={e => setDecisionReason(e.target.value)}
                     placeholder="e.g. mark amended after a remark; board resolution 7/26"
                     className="mt-1" data-testid="cert-decision-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cert-decision-cancel"
                    onClick={() => { setDecisionFor(null); setDecisionReason(''); }}>Cancel</Button>
            <Button
              onClick={() => {
                if (decisionReason.trim().length < 10) { toast.error('Say why — a sentence is enough'); return; }
                decide.mutate({ certId: decisionFor.certificate_id, action: decisionAction, reason: decisionReason });
              }}
              disabled={decide.isPending}
              data-testid="cert-decision-confirm"
            >
              {decide.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Record decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


const GraduationPanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [gradDate, setGradDate] = useState('');
  const [gradNote, setGradNote] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: preview } = useQuery({
    queryKey: ['promotion-preview', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/promotions/preview`),
    enabled: !!tenantId,
  });

  const bulkGradMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/graduations/bulk`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (d) => {
      toast.success(d.message);
      setShowConfirm(false);
      setSelectedClasses([]);
      queryClient.invalidateQueries({ queryKey: ['promotion-preview'] });
      queryClient.invalidateQueries({ queryKey: ['school-students'] });
      queryClient.invalidateQueries({ queryKey: ['alumni'] });
      queryClient.invalidateQueries({ queryKey: ['alumni-stats'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const toggleClass = (classId) => {
    setSelectedClasses(prev => prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]);
  };

  const previewClasses = preview?.classes || [];
  const selectedStudentCount = previewClasses
    .filter(c => selectedClasses.includes(c.class_id))
    .reduce((sum, c) => sum + c.student_count, 0);

  return (
    <div className="space-y-4" data-testid="graduation-panel">
      <CertificateAudit tenantId={tenantId} />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-indigo-600" /> Bulk Graduation
          </CardTitle>
          <CardDescription>Select final-year classes to graduate all their active students</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Graduation Date</Label>
              <Input type="date" value={gradDate} onChange={e => setGradDate(e.target.value)} data-testid="graduation-date-input" />
            </div>
            <div>
              <Label>Note</Label>
              <Input value={gradNote} onChange={e => setGradNote(e.target.value)} placeholder="e.g. Class of 2025" data-testid="graduation-note-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Select Classes to Graduate</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {previewClasses.map(c => (
                <button
                  key={c.class_id}
                  onClick={() => toggleClass(c.class_id)}
                  className={`flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${
                    selectedClasses.includes(c.class_id)
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`grad-class-${c.class_id}`}
                >
                  <div>
                    <p className="font-medium text-sm">{c.class_name}</p>
                    <p className="text-xs text-slate-500">{c.student_count} students</p>
                  </div>
                  {selectedClasses.includes(c.class_id) && <CheckCircle2 className="h-5 w-5 text-indigo-600" />}
                </button>
              ))}
            </div>
          </div>

          {selectedClasses.length > 0 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-indigo-600">{selectedStudentCount}</span> students will be graduated
              </p>
              <Button onClick={() => setShowConfirm(true)} data-testid="execute-graduation-btn">
                <GraduationCap className="h-4 w-4 mr-2" />Graduate Selected
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirm Graduation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">
              <strong>{selectedStudentCount}</strong> students from <strong>{selectedClasses.length}</strong> classes will be marked as graduated.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              Graduated students will lose active enrollment and portal access.
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => bulkGradMutation.mutate({ class_ids: selectedClasses, graduation_date: gradDate, note: gradNote })}
              disabled={bulkGradMutation.isPending}
              data-testid="confirm-graduation-btn"
            >
              {bulkGradMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== ALUMNI TAB ====================
const AlumniPanel = ({ tenantId }) => {
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [selectedAlumni, setSelectedAlumni] = useState(null);

  const { data: alumniData, isLoading } = useQuery({
    queryKey: ['alumni', tenantId, search, yearFilter],
    queryFn: () => {
      let q = `/tenants/${tenantId}/alumni?`;
      if (search) q += `search=${encodeURIComponent(search)}&`;
      if (yearFilter) q += `graduation_year=${yearFilter}&`;
      return api(q);
    },
    enabled: !!tenantId,
  });

  const { data: stats } = useQuery({
    queryKey: ['alumni-stats', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/alumni/stats`),
    enabled: !!tenantId,
  });

  const { data: alumniProfile } = useQuery({
    queryKey: ['alumni-profile', tenantId, selectedAlumni],
    queryFn: () => api(`/tenants/${tenantId}/alumni/${selectedAlumni}`),
    enabled: !!tenantId && !!selectedAlumni,
  });

  const alumni = alumniData?.alumni || [];
  const totalAlumni = alumniData?.total || 0;

  const statCards = [
    { label: 'Alumni', value: stats?.total_alumni || 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Active', value: stats?.total_active || 0, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Withdrawn', value: stats?.total_withdrawn || 0, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Suspended', value: stats?.total_suspended || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  return (
    <div className="space-y-4" data-testid="alumni-panel">
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Year Breakdown */}
      {stats?.by_year?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Graduation Years</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.by_year.map(y => (
                <button
                  key={y.year}
                  onClick={() => setYearFilter(yearFilter === y.year ? '' : y.year)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    yearFilter === y.year
                      ? 'bg-indigo-100 border-indigo-300 text-indigo-800'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`year-filter-${y.year}`}
                >
                  {y.year} <span className="font-semibold ml-1">{y.count}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Alumni List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Alumni Directory ({totalAlumni})</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Search alumni..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="alumni-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Adm #</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Last Class</TableHead>
                <TableHead>Graduated</TableHead>
                <TableHead>Guardian</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : alumni.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No alumni found</TableCell></TableRow>
              ) : alumni.map(a => (
                <TableRow key={a.id} data-testid={`alumni-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.admission_number}</TableCell>
                  <TableCell className="font-medium">{a.first_name} {a.last_name}</TableCell>
                  <TableCell>{a.last_class || '-'}</TableCell>
                  <TableCell className="text-sm">{a.graduation_date || '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {a.guardians?.[0]?.name || '-'}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedAlumni(a.id)} data-testid={`view-alumni-${a.id}`}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Alumni Profile Dialog */}
      <Dialog open={!!selectedAlumni} onOpenChange={() => setSelectedAlumni(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alumni Profile</DialogTitle>
          </DialogHeader>
          {alumniProfile ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-3 border-b">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <GraduationCap className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{alumniProfile.first_name} {alumniProfile.last_name}</h3>
                  <p className="text-sm text-slate-500">Adm# {alumniProfile.admission_number} | Graduated: {alumniProfile.graduation_date || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Exams Taken</p>
                    <p className="text-xl font-bold text-indigo-600">{alumniProfile.exam_summary?.total_exams_taken || 0}</p>
                    <p className="text-xs text-slate-400">Avg: {alumniProfile.exam_summary?.average_percentage || 0}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Fees Paid</p>
                    <p className="text-xl font-bold text-green-600">${alumniProfile.fee_summary?.total_paid?.toFixed(2) || '0.00'}</p>
                    <p className="text-xs text-slate-400">{alumniProfile.fee_summary?.payment_count || 0} payments</p>
                  </CardContent>
                </Card>
              </div>

              {/* Class History */}
              <div>
                <h4 className="font-medium text-sm mb-2">Class History</h4>
                <div className="space-y-2">
                  {(alumniProfile.class_history || []).map((ch, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded">
                      <School className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">{ch.class_name}</span>
                      <Badge variant="outline" className="text-xs">{ch.status}</Badge>
                      <span className="text-slate-400 text-xs ml-auto">{ch.enrolled_at ? new Date(ch.enrolled_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Guardians */}
              {alumniProfile.guardians?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2">Guardians</h4>
                  {alumniProfile.guardians.map((g, i) => (
                    <div key={i} className="text-sm p-2 bg-slate-50 rounded mb-1">
                      <span className="font-medium">{g.name}</span>
                      <span className="text-slate-400 ml-2">({g.relationship})</span>
                      {g.phone && <span className="text-slate-500 ml-2">{g.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== STUDENT TIMELINE ====================
export const StudentTimeline = ({ tenantId, studentId }) => {
  const { data: timeline, isLoading } = useQuery({
    queryKey: ['student-timeline', tenantId, studentId],
    queryFn: () => api(`/tenants/${tenantId}/students/${studentId}/timeline`),
    enabled: !!tenantId && !!studentId,
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>;
  }

  const events = timeline?.events || [];
  const student = timeline?.student;

  const iconMap = {
    clipboard: ClipboardCheck,
    'check-circle': CheckCircle2,
    'user-plus': UserPlus,
    school: School,
    'arrow-up': ArrowUp,
    pause: Pause,
    banknote: Banknote,
    flag: Flag,
  };

  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    gold: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    orange: 'bg-orange-100 text-orange-600',
    gray: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="space-y-4" data-testid="student-timeline">
      {student && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold">{student.first_name} {student.last_name}</h3>
            <p className="text-xs text-slate-500">
              Adm# {student.admission_number} | Status: <span className="font-medium">{student.status}</span>
            </p>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-slate-400 text-center py-8">No lifecycle events recorded</p>
      ) : (
        <div className="relative pl-8">
          {/* Vertical line */}
          <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-slate-200" />

          {events.map((event, idx) => {
            const Icon = iconMap[event.icon] || Clock;
            const colorClass = colorMap[event.color] || colorMap.gray;
            return (
              <div key={idx} className="relative mb-6 last:mb-0" data-testid={`timeline-event-${idx}`}>
                <div className={`absolute -left-8 top-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="pl-4">
                  <h4 className="font-medium text-sm">{event.title}</h4>
                  {event.description && <p className="text-xs text-slate-500">{event.description}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">
                    {event.date ? new Date(event.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ==================== MAIN LIFECYCLE PAGE ====================
export const SchoolPromotions = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="school-promotions">
      <h2 className="text-lg font-semibold text-slate-900">Promotions</h2>
      <PromotionsPanel tenantId={tenantId} />
    </div>
  );
};

export const SchoolGraduation = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="school-graduation">
      <h2 className="text-lg font-semibold text-slate-900">Graduation</h2>
      <GraduationPanel tenantId={tenantId} />
    </div>
  );
};

export const SchoolAlumni = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="school-alumni">
      <h2 className="text-lg font-semibold text-slate-900">Alumni</h2>
      <AlumniPanel tenantId={tenantId} />
    </div>
  );
};
